import {
  ACTION_CHECKLIST_MAX_ITEMS,
  ACTION_CLOSED_STATUSES,
  ACTION_NOTE_MAX,
  ACTION_STATUS_LABELS,
  ACTION_STATUSES,
  actionChecklistInputSchema,
  actionUpdateInputSchema,
  actionUpdateListSchema,
  formatDateTime,
  planActionSchema,
  type ActionStatusName,
  type PlanAction,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ProgressBar } from '@/components/progress/ProgressBar';
import { ErrorState, LoadingState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { CollaborationPanel } from '@/features/collaboration/CollaborationPanel';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const progressNote = (a: PlanAction) => {
  if (a.progressSource === 'STATUS') return 'Ação concluída.';
  if (a.progressSource === 'CHECKLIST') {
    const done = a.checklist.filter((i) => i.done).length;
    return `Pelo checklist: ${done} de ${a.checklist.length} ${a.checklist.length === 1 ? 'item marcado' : 'itens marcados'}.`;
  }
  return a.reportedProgress === null
    ? 'Nenhum percentual informado ainda.'
    : 'Percentual informado na última atualização.';
};

/**
 * Acompanhamento da ação pela execução (T-094a, RF-52, RN-26; DEC-045): progresso, checklist (marcar, incluir,
 * renomear, reordenar e remover), atualização com status, percentual e justificativa, e o histórico.
 */
export function ActionExecution({
  actionUrl,
  organizationId,
  projectId,
  action,
  canEdit,
  onChanged,
}: {
  /** Empresa e projeto (comentários e anexos da ação — T-112b). */
  organizationId: string;
  projectId: string;
  /** …/action-plans/:planId/actions/:actionId */
  actionUrl: string;
  action: PlanAction;
  canEdit: boolean;
  /** A lista de planos atualiza depois de cada mudança. */
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(action);
  const closed = ACTION_CLOSED_STATUSES.includes(current.status);
  const historyKey = ['action-updates', current.id];
  const history = useQuery({
    queryKey: historyKey,
    queryFn: ({ signal }) =>
      client.request(`${actionUrl}/updates`, { schema: actionUpdateListSchema, signal }),
  });

  // A lista atrás do diálogo atualiza em segundo plano: o checklist fica livre para o próximo clique.
  const applied = (next: PlanAction, message?: string) => {
    setCurrent(next);
    if (message) toast.success(message, { description: next.title });
    void onChanged();
  };
  const checklist = useMutation({
    meta: { handlesError: true },
    mutationFn: (req: { path: string; method: 'POST' | 'PUT' | 'DELETE'; body?: unknown }) =>
      client.request(`${actionUrl}/checklist${req.path}`, {
        method: req.method,
        ...(req.body === undefined ? {} : { body: req.body }),
        schema: planActionSchema,
      }),
    onSuccess: (next) => applied(next),
    onError: (err) => toast.error('Não foi possível mudar o checklist', { description: errorMessage(err) }),
  });
  const busy = checklist.isPending;

  return (
    <div className="flex flex-col gap-5">
      <section aria-label="Progresso da ação">
        <ProgressBar value={current.progress} label={`Progresso de ${current.title}`} />
        <p className="mt-1 text-xs text-muted-foreground">{progressNote(current)}</p>
      </section>

      <section aria-label="Checklist" className="flex flex-col gap-2">
        <h3 className="text-sm font-bold">Checklist</h3>
        {current.checklist.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Sem checklist: o progresso é o percentual informado na atualização.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {current.checklist.map((item, index) => (
              <ChecklistRow
                key={item.id}
                item={item}
                first={index === 0}
                last={index === current.checklist.length - 1}
                canCheck={canEdit && !closed}
                busy={busy}
                onCheck={(done) =>
                  checklist.mutate({ path: `/${item.id}/check`, method: 'POST', body: { done } })
                }
                onRename={(text) =>
                  checklist.mutateAsync({ path: `/${item.id}`, method: 'PUT', body: { text } })
                }
                onRemove={() => checklist.mutate({ path: `/${item.id}`, method: 'DELETE' })}
                onMove={(delta) => {
                  const ids = current.checklist.map((i) => i.id);
                  const [moved] = ids.splice(index, 1);
                  ids.splice(index + delta, 0, moved!);
                  checklist.mutate({ path: '/order', method: 'PUT', body: { ids } });
                }}
              />
            ))}
          </ul>
        )}
        {closed ? (
          <p className="text-xs text-muted-foreground">
            Ação encerrada: reabra a ação para mexer no checklist.
          </p>
        ) : canEdit ? (
          <NewItemForm
            disabled={busy || current.checklist.length >= ACTION_CHECKLIST_MAX_ITEMS}
            full={current.checklist.length >= ACTION_CHECKLIST_MAX_ITEMS}
            onAdd={(text) => checklist.mutateAsync({ path: '', method: 'POST', body: { text } })}
          />
        ) : null}
      </section>

      {canEdit ? (
        <UpdateForm
          key={`${current.status}-${current.progressSource}`}
          actionUrl={actionUrl}
          action={current}
          onSaved={async (next) => {
            await queryClient.invalidateQueries({ queryKey: historyKey });
            applied(next, 'Atualização registrada');
          }}
        />
      ) : null}

      <CollaborationPanel
        organizationId={organizationId}
        projectId={projectId}
        subject="action"
        subjectId={current.id}
        label={current.title}
      />

      <section aria-label="Histórico de atualizações" className="flex flex-col gap-2">
        <h3 className="text-sm font-bold">Histórico de atualizações</h3>
        {history.isPending ? (
          <LoadingState rows={2} label="Carregando o histórico" />
        ) : history.isError ? (
          <ErrorState
            error={history.error}
            onRetry={() => void history.refetch()}
            retrying={history.isFetching}
          />
        ) : history.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma atualização registrada ainda.</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {history.data.items.map((h) => (
              <li key={h.id} className="rounded-md border border-border px-3 py-2 text-sm">
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(h.createdAt)} · {h.createdByName}
                </p>
                <p className="font-medium">
                  {ACTION_STATUS_LABELS[h.status]} · {h.progress}%
                </p>
                {h.note ? <p className="mt-0.5 whitespace-pre-line">{h.note}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function ChecklistRow({
  item,
  first,
  last,
  canCheck,
  busy,
  onCheck,
  onRename,
  onRemove,
  onMove,
}: {
  item: PlanAction['checklist'][number];
  first: boolean;
  last: boolean;
  canCheck: boolean;
  busy: boolean;
  onCheck: (done: boolean) => void;
  onRename: (text: string) => Promise<unknown>;
  onRemove: () => void;
  onMove: (delta: -1 | 1) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(item.text);
  const [error, setError] = useState<string | undefined>();
  const inputId = useId();

  async function save(event: FormEvent) {
    event.preventDefault();
    const parsed = actionChecklistInputSchema.safeParse({ text });
    if (!parsed.success) return setError(zodFieldErrors(parsed.error).text);
    try {
      await onRename(parsed.data.text);
      setEditing(false);
      setError(undefined);
    } catch (err) {
      setError(apiFieldErrors(err).text ?? errorMessage(err));
    }
  }

  if (editing) {
    return (
      <li className="px-3 py-2">
        <form noValidate onSubmit={save} className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="flex-1">
            <FormField id={inputId} label={`Novo texto de "${item.text}"`} error={error}>
              <Input
                {...fieldA11y(inputId, error)}
                value={text}
                maxLength={300}
                onChange={(e) => setText(e.target.value)}
                className="h-9"
              />
            </FormField>
          </div>
          <div className="flex gap-1 sm:pt-6">
            <Button type="submit" size="sm" loading={busy}>
              <Check aria-hidden />
              Salvar item
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false);
                setText(item.text);
                setError(undefined);
              }}
            >
              <X aria-hidden />
              Desistir
            </Button>
          </div>
        </form>
      </li>
    );
  }
  return (
    <li className="flex flex-wrap items-start gap-2 px-3 py-2">
      <label className="flex min-w-0 flex-1 items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 accent-(--brand)"
          checked={item.done}
          disabled={!canCheck || busy}
          aria-label={`Marcar "${item.text}"`}
          onChange={(e) => onCheck(e.target.checked)}
        />
        <span className="min-w-0">
          <span className={item.done ? 'text-muted-foreground line-through' : undefined}>{item.text}</span>
          {item.done && item.doneAt ? (
            <span className="block text-xs text-muted-foreground">
              Marcado por {item.doneByName} em {formatDateTime(item.doneAt)}
            </span>
          ) : null}
        </span>
      </label>
      {canCheck ? (
        <div className="flex shrink-0 gap-0.5">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Subir "${item.text}"`}
            disabled={busy || first}
            onClick={() => onMove(-1)}
          >
            <ArrowUp aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Descer "${item.text}"`}
            disabled={busy || last}
            onClick={() => onMove(1)}
          >
            <ArrowDown aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Renomear "${item.text}"`}
            disabled={busy}
            onClick={() => setEditing(true)}
          >
            <Pencil aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Remover "${item.text}"`}
            disabled={busy}
            onClick={onRemove}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function NewItemForm({
  disabled,
  full,
  onAdd,
}: {
  disabled: boolean;
  full: boolean;
  onAdd: (text: string) => Promise<unknown>;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const id = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const parsed = actionChecklistInputSchema.safeParse({ text });
    if (!parsed.success) return setError(zodFieldErrors(parsed.error).text);
    setSaving(true);
    try {
      await onAdd(parsed.data.text);
      setText('');
      setError(undefined);
    } catch (err) {
      setError(apiFieldErrors(err).text ?? errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-2 sm:flex-row sm:items-start">
      <div className="flex-1">
        <FormField
          id={id}
          label="Novo item do checklist"
          error={error}
          hint={
            full ? (
              <p className="text-xs text-muted-foreground">
                O checklist chegou a {ACTION_CHECKLIST_MAX_ITEMS} itens: agrupe ou remova itens antes.
              </p>
            ) : undefined
          }
        >
          <Input
            {...fieldA11y(id, error, full)}
            value={text}
            maxLength={300}
            disabled={disabled}
            placeholder="Ex.: Conferir os lançamentos do mês"
            onChange={(e) => setText(e.target.value)}
            className="h-9"
          />
        </FormField>
      </div>
      <Button
        type="submit"
        size="sm"
        variant="outline"
        className="sm:mt-6"
        loading={saving}
        disabled={disabled}
      >
        <Plus aria-hidden />
        Adicionar item
      </Button>
    </form>
  );
}

function UpdateForm({
  actionUrl,
  action,
  onSaved,
}: {
  actionUrl: string;
  action: PlanAction;
  onSaved: (next: PlanAction) => Promise<void>;
}) {
  const { client } = useAuth();
  const [status, setStatus] = useState<ActionStatusName>(action.status);
  const [progress, setProgress] = useState(
    action.progressSource === 'MANUAL' && action.reportedProgress !== null
      ? String(action.reportedProgress)
      : '',
  );
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { status: useId(), progress: useId(), note: useId() };
  const finalClosed = ACTION_CLOSED_STATUSES.includes(status);
  const progressLocked = action.checklist.length > 0 || finalClosed;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const typed = progress.trim();
    const changedProgress =
      !progressLocked && typed !== '' && typed !== String(action.reportedProgress ?? '') ? typed : undefined;
    const parsed = actionUpdateInputSchema.safeParse({
      ...(status !== action.status ? { status } : {}),
      ...(changedProgress !== undefined ? { progress: changedProgress } : {}),
      note,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      const saved = await client.request(`${actionUrl}/updates`, {
        method: 'POST',
        body: parsed.data,
        schema: planActionSchema,
      });
      setNote('');
      await onSaved(saved);
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section aria-label="Atualizar a ação">
      <h3 className="text-sm font-bold">Atualizar</h3>
      <form noValidate onSubmit={submit} className="mt-2 flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField id={ids.status} label="Status" error={errors.status}>
            <NativeSelect
              {...fieldA11y(ids.status, errors.status)}
              value={status}
              onChange={(e) => setStatus(e.target.value as ActionStatusName)}
            >
              {ACTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ACTION_STATUS_LABELS[s]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField
            id={ids.progress}
            label="Percentual concluído (%)"
            error={errors.progress}
            hint={
              progressLocked ? (
                <p className="text-xs text-muted-foreground">
                  {action.checklist.length
                    ? 'Vem do checklist: marque os itens.'
                    : 'Ação encerrada não recebe percentual.'}
                </p>
              ) : undefined
            }
          >
            <Input
              {...fieldA11y(ids.progress, errors.progress, progressLocked)}
              value={progressLocked ? '' : progress}
              inputMode="numeric"
              maxLength={3}
              disabled={progressLocked}
              placeholder={progressLocked ? undefined : 'De 0 a 100'}
              onChange={(e) => setProgress(e.target.value)}
              className="h-11 w-32"
            />
          </FormField>
        </div>
        <FormField
          id={ids.note}
          label="Justificativa"
          error={errors.note}
          hint={
            <p className="text-xs text-muted-foreground">
              {action.late
                ? 'A ação está atrasada: explique a situação (a empresa pode exigir — RN-26).'
                : 'O que mudou, o que falta ou o que está impedindo.'}
            </p>
          }
        >
          <textarea
            {...fieldA11y(ids.note, errors.note, true)}
            value={note}
            rows={3}
            maxLength={ACTION_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
            className={textareaClass}
          />
        </FormField>
        {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
        <div className="flex justify-end">
          <Button type="submit" loading={saving}>
            Registrar atualização
          </Button>
        </div>
      </form>
    </section>
  );
}
